import React, { useState } from "react";
import { Row, Col, Card } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";
import { useSelector } from "react-redux";

import "../../styles/ticket-system/analytics-dashboard.css";
import "../../styles/ticket-system/ticket-status-badges.css";
import PaymentLinkTable from "../payment-link-system/PaymentLinkTable";
import {
  supervisorTicketAnalyticsColumns,
  trackerAnalyticsColumns,
} from "./utils/ticketAnalyticsColumn";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Priority Heatmap Component
const PriorityHeatmap = ({ data }) => {
  if (!data || !data.heatmap) {
    return (
      <div className="sla-risk-empty">
        <p className="text-muted">No data available</p>
      </div>
    );
  }

  const { heatmap, priority_levels, total_tickets } = data;
  const issueTypes = Object.keys(heatmap);

  // Calculate risk category counts
  const getRiskCounts = () => {
    let critical = 0,
      warning = 0,
      caution = 0,
      safe = 0,
      breached = 0;

    issueTypes.forEach((issueType) => {
      priority_levels.forEach((priority) => {
        const cellData = heatmap[issueType][priority];
        const percentage = cellData.sla_breach_percentage;
        const totalCount = cellData.total_count;
        const breachedCount = cellData.sla_breached_count;

        if (breachedCount > 0) {
          breached += breachedCount;
        }

        if (totalCount > 0) {
          if (percentage >= 76) critical += totalCount;
          else if (percentage >= 51) warning += totalCount;
          else if (percentage >= 26) caution += totalCount;
          else safe += totalCount;
        }
      });
    });

    return { critical, warning, caution, safe, breached };
  };

  const riskCounts = getRiskCounts();

  return (
    <div className="sla-risk-analysis">
      <Row className="g-3">
        <Col>
          <div className="risk-card critical">
            <div className="risk-icon">🔴</div>
            <div className="risk-content">
              <div className="risk-title">Critical</div>
              <div className="risk-count">Count: {riskCounts.critical}</div>
              <div className="risk-percentage">
                Percentage:{" "}
                {total_tickets > 0
                  ? ((riskCounts.critical / total_tickets) * 100).toFixed(2)
                  : 0.0}
                %
              </div>
              <div className="risk-threshold">Threshold: 76%+</div>
            </div>
          </div>
        </Col>
        <Col>
          <div className="risk-card warning">
            <div className="risk-icon">🟠</div>
            <div className="risk-content">
              <div className="risk-title">Warning</div>
              <div className="risk-count">Count: {riskCounts.warning}</div>
              <div className="risk-percentage">
                Percentage:{" "}
                {total_tickets > 0
                  ? ((riskCounts.warning / total_tickets) * 100).toFixed(2)
                  : 0.0}
                %
              </div>
              <div className="risk-threshold">Threshold: 51-75%</div>
            </div>
          </div>
        </Col>
        <Col>
          <div className="risk-card caution">
            <div className="risk-icon">🟡</div>
            <div className="risk-content">
              <div className="risk-title">Caution</div>
              <div className="risk-count">Count: {riskCounts.caution}</div>
              <div className="risk-percentage">
                Percentage:{" "}
                {total_tickets > 0
                  ? ((riskCounts.caution / total_tickets) * 100).toFixed(2)
                  : 0.0}
                %
              </div>
              <div className="risk-threshold">Threshold: 26-50%</div>
            </div>
          </div>
        </Col>
        <Col>
          <div className="risk-card safe">
            <div className="risk-icon">🟢</div>
            <div className="risk-content">
              <div className="risk-title">Safe</div>
              <div className="risk-count">Count: {riskCounts.safe}</div>
              <div className="risk-percentage">
                Percentage:{" "}
                {total_tickets > 0
                  ? ((riskCounts.safe / total_tickets) * 100).toFixed(2)
                  : 0.0}
                %
              </div>
              <div className="risk-threshold">Threshold: 0-25%</div>
            </div>
          </div>
        </Col>
        <Col>
          <div className="risk-card breached">
            <div className="risk-icon">⚠️</div>
            <div className="risk-content">
              <div className="risk-title">Breached</div>
              <div className="risk-count">Count: {riskCounts.breached}</div>
              <div className="risk-percentage">
                Percentage:{" "}
                {total_tickets > 0
                  ? ((riskCounts.breached / total_tickets) * 100).toFixed(2)
                  : 0.0}
                %
              </div>
              <div className="risk-threshold">Already Breached</div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

const AnalyticsDashboard = ({}) => {
  // Get analytics data from Redux store
  const { analyticsLoading, analyticsPagination, analyticsData } = useSelector(
    (state) => state.ticketSystem
  );

  // Local pagination state for tables
  const [supPage, setSupPage] = useState(0);
  const [trackerPage, setTrackerPage] = useState(0);
  const supPageLimit = 10;
  const trackerPageLimit = 10;

  // Chart configurations
  const getGymwiseChartData = () => {
    const { gymwiseStats } = analyticsData;

    const labels = gymwiseStats.map((gym) =>
      gym?.gym_name.replace("WTF: ", "")
    );
    const totalTickets = gymwiseStats.map((gym) => gym.tickets);
    const closedTickets = gymwiseStats.map(
      (gym) => gym.verified_and_closed_tickets
    );

    return {
      labels,
      datasets: [
        {
          label: "Total Tickets",
          data: totalTickets,
          backgroundColor: "#dc3545",
          borderRadius: 4,
        },
        {
          label: "Closed Tickets",
          data: closedTickets,
          backgroundColor: "#28a745",
          borderRadius: 4,
        },
      ],
    };
  };

  const getCategoryChartData = () => {
    const { categoryDistribution } = analyticsData;
    const distribution = categoryDistribution.distribution || [];

    const labels = distribution.map((cat) => cat.category);
    const data = distribution.map((cat) => cat.count);
    const colors = [
      "#dc3545", // Red
      "#ffc107", // Yellow
      "#28a745", // Green
      "#007bff", // Blue
      "#6f42c1", // Purple
      "#fd7e14", // Orange
    ];

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: colors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  const getTATTrendChartData = () => {
    const { tatTrend } = analyticsData;

    const labels = tatTrend.map((item) => item.month);
    const data = tatTrend.map((item) => parseFloat(item.avg_tat_hours));

    return {
      labels,
      datasets: [
        {
          label: "Average TAT (Hours)",
          data,
          borderColor: "#ffc107",
          backgroundColor: "#ffc107",
          fill: false,
          tension: 0.4,
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  const getInfraSpendChartData = () => {
    const { infraSpend } = analyticsData;
    const gymData = infraSpend.data || [];

    const labels = gymData.map((gym) => gym.gym_name.replace("WTF: ", ""));
    const data = gymData.map((gym) => parseFloat(gym.total_spend));

    return {
      labels,
      datasets: [
        {
          label: "Infrastructure Spend (₹)",
          data,
          backgroundColor: "#007bff",
          borderRadius: 4,
        },
      ],
    };
  };

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <Row className="analytics-header">
        <Col>
          <div className="analytics-title-section">
            <h2 className="analytics-title">Analytics Dashboard</h2>
            <p className="analytics-subtitle">
              Complete visibility into facility management performance
            </p>
          </div>
        </Col>
      </Row>

      {/* Charts Area */}
      <Row>
        {/* Gymwise Tickets Chart */}
        <Col lg={6} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Tickets by Gym (Last 30 Days)</h6>
            </Card.Header>
            <Card.Body>
              {analyticsLoading.gymwise ? (
                <div className="chart-loading">Loading...</div>
              ) : (
                <Bar
                  data={getGymwiseChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "bottom",
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                      },
                    },
                  }}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Category Distribution */}
        <Col lg={6} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Issue Category Distribution</h6>
            </Card.Header>
            <Card.Body>
              {analyticsLoading.category ? (
                <div className="chart-loading">Loading...</div>
              ) : (
                <Doughnut
                  data={getCategoryChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                      },
                    },
                  }}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        {/* TAT Trend */}
        <Col lg={6} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Average TAT Trend</h6>
            </Card.Header>
            <Card.Body>
              {analyticsLoading.tatTrend ? (
                <div className="chart-loading">Loading...</div>
              ) : (
                <Line
                  data={getTATTrendChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: "Hours",
                        },
                      },
                    },
                  }}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Infrastructure Spend */}
        <Col lg={6} className="mb-4">
          <Card className="chart-card h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Infrastructure Spend by Gym</h6>
            </Card.Header>
            <Card.Body>
              {analyticsLoading.infra ? (
                <div className="chart-loading">Loading...</div>
              ) : (
                <Bar
                  data={getInfraSpendChartData()}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        title: {
                          display: true,
                          text: "Amount (₹)",
                        },
                      },
                    },
                  }}
                  height={300}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Priority Heatmap */}
      <Row className="mb-4">
        <Col>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Issue Type vs Priority Risk Matrix</h6>
              <div className="heatmap-legend">
                <small className="text-muted">SLA Breach Risk %</small>
              </div>
            </Card.Header>
            <Card.Body>
              {analyticsLoading.priorityHeatmap ? (
                <div className="chart-loading">Loading...</div>
              ) : (
                <PriorityHeatmap data={analyticsData.priorityHeatmap} />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Supervisor Performance Heatmap */}
      <Row className="mb-4">
        <Col>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Supervisor Performance Heatmap</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="fullWidthFlexCenter separatedTable no-border-table">
                {analyticsLoading.supervisorPerformance ? (
                  <div className="chart-loading">Loading...</div>
                ) : (
                  <PaymentLinkTable
                    tableData={analyticsData.supervisorPerformance}
                    tableColumns={supervisorTicketAnalyticsColumns}
                    tablePagination={analyticsPagination.supervisorPerformance}
                    isPage={supPage}
                    setIsPage={setSupPage}
                    pageLimit={supPageLimit}
                    setPageLimit={() => {}}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Paid Resolution Tracker */}
      <Row className="mb-4">
        <Col>
          <Card className="h-100">
            <Card.Header className="d-flex align-items-center justify-content-between">
              <h6 className="mb-0">Paid Resolution Tracker</h6>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="fullWidthFlexCenter separatedTable no-border-table">
                {analyticsLoading.paidResolutionTracker ? (
                  <div className="chart-loading">Loading...</div>
                ) : (
                  <PaymentLinkTable
                    tableData={analyticsData.paidResolutionTracker}
                    tableColumns={trackerAnalyticsColumns}
                    tablePagination={analyticsPagination.paidResolutionTracker}
                    isPage={trackerPage}
                    setIsPage={setTrackerPage}
                    pageLimit={trackerPageLimit}
                    setPageLimit={() => {}}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AnalyticsDashboard;
